import React, { useEffect, useState } from 'react';
import { PlusIcon, GaugeIcon, ClockIcon, CarIcon, PhoneIcon, FuelIcon, AlertCircleIcon, CheckCircleIcon, SearchIcon, LogOutIcon, UserIcon, ShieldIcon, MapIcon, SettingsIcon } from 'lucide-react';
import { QueueItem } from './QueueItem';
import { AddVehicleModal } from './AddVehicleModal';
import { StationSelector } from './StationSelector';
import { QuickAccessModal } from './QuickAccessModal';
import { StationsOverview } from './StationsOverview';
import { SettingsModal } from './SettingsModal';
import { bamakoStations, GasStation } from '../data/stations';
import { useAuth } from '../context/AuthContext';
import { users as initialUsers } from '../data/users';
import { User } from '../types/user';
import { saveToDatabase, loadFromDatabase } from '../utils/database';
interface Vehicle {
  id: string;
  ticketNumber: string;
  name: string;
  phone: string;
  plateNumber: string;
  vehicleType: string;
  category: string;
  pumpNumber: number;
  serviceType: string;
  estimatedPrice: number;
  addedAt: Date;
  status: 'waiting' | 'in-service' | 'completed';
  serviceStartedAt?: Date;
  completedAt?: Date;
  addedBy: string;
  addedByName: string;
  lastModifiedBy?: string;
  lastModifiedByName?: string;
}
interface StatusChangeEvent {
  vehicleId: string;
  fromStatus: Vehicle['status'];
  toStatus: Vehicle['status'];
  timestamp: Date;
  stationId: string;
  userId: string;
  userName: string;
}
export function QueueDashboard() {
  const {
    user,
    logout,
    canAddVehicle,
    canRemoveVehicle,
    canUpdateStatus
  } = useAuth();
  const [selectedStation, setSelectedStation] = useState<GasStation | null>(null);
  const [stations, setStations] = useState<GasStation[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [stationQueues, setStationQueues] = useState<Record<string, Vehicle[]>>({});
  const [statusChangeHistory, setStatusChangeHistory] = useState<StatusChangeEvent[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuickAccessOpen, setIsQuickAccessOpen] = useState(false);
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  // Charger les données au démarrage
  useEffect(() => {
    const savedData = loadFromDatabase();
    // Charger les stations (utiliser les données sauvegardées ou les données initiales)
    if (savedData.stations && savedData.stations.length > 0) {
      setStations(savedData.stations);
    } else {
      setStations(bamakoStations);
      saveToDatabase({
        stations: bamakoStations
      });
    }
    // Charger les utilisateurs (utiliser les données sauvegardées ou les données initiales)
    if (savedData.users && savedData.users.length > 0) {
      setUsers(savedData.users);
    } else {
      setUsers(initialUsers);
      saveToDatabase({
        users: initialUsers
      });
    }
    // Charger les files d'attente
    if (savedData.stationQueues) {
      setStationQueues(savedData.stationQueues);
    }
    // Charger l'historique des changements de statut
    if (savedData.statusChangeHistory) {
      setStatusChangeHistory(savedData.statusChangeHistory);
    }
    setIsDataLoaded(true);
    console.log('📊 Données chargées depuis la base de données locale');
  }, []);
  // Sauvegarder les stations quand elles changent
  useEffect(() => {
    if (isDataLoaded && stations.length > 0) {
      saveToDatabase({
        stations
      });
    }
  }, [stations, isDataLoaded]);
  // Sauvegarder les utilisateurs quand ils changent
  useEffect(() => {
    if (isDataLoaded && users.length > 0) {
      saveToDatabase({
        users
      });
    }
  }, [users, isDataLoaded]);
  // Sauvegarder les files d'attente quand elles changent
  useEffect(() => {
    if (isDataLoaded) {
      saveToDatabase({
        stationQueues
      });
    }
  }, [stationQueues, isDataLoaded]);
  // Sauvegarder l'historique des changements de statut
  useEffect(() => {
    if (isDataLoaded && statusChangeHistory.length > 0) {
      saveToDatabase({
        statusChangeHistory
      });
    }
  }, [statusChangeHistory, isDataLoaded]);
  useEffect(() => {
    if (user && user.assignedStationId) {
      const assignedStation = stations.find(s => s.id === user.assignedStationId);
      if (assignedStation) {
        setSelectedStation(assignedStation);
      }
    } else if (!selectedStation && stations.length > 0) {
      setSelectedStation(stations[0]);
    }
  }, [user, stations]);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const currentQueue = selectedStation ? stationQueues[selectedStation.id] || [] : [];
  const generateTicketNumber = () => {
    if (!selectedStation) return '';
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    // Compter TOUS les véhicules dans la file actuelle pour garantir l'unicité
    const totalVehicles = currentQueue.length;
    const nextNumber = (totalVehicles + 1).toString().padStart(4, '0');
    return `${selectedStation.code}-${dateStr}-${nextNumber}`;
  };
  const addVehicle = (vehicle: Omit<Vehicle, 'id' | 'addedAt' | 'status' | 'ticketNumber' | 'addedBy' | 'addedByName'>) => {
    if (!selectedStation || !user) return;
    const newVehicle: Vehicle = {
      ...vehicle,
      id: Date.now().toString(),
      ticketNumber: generateTicketNumber(),
      addedAt: new Date(),
      status: 'waiting',
      addedBy: user.id,
      addedByName: user.fullName
    };
    setStationQueues(prev => ({
      ...prev,
      [selectedStation.id]: [...(prev[selectedStation.id] || []), newVehicle]
    }));
    setIsModalOpen(false);
  };
  const updateVehicleStatus = (id: string, status: Vehicle['status']) => {
    if (!selectedStation || !canUpdateStatus(selectedStation.id) || !user) return;
    const currentVehicle = (stationQueues[selectedStation.id] || []).find(v => v.id === id);
    if (currentVehicle && currentVehicle.status !== status) {
      const changeEvent: StatusChangeEvent = {
        vehicleId: id,
        fromStatus: currentVehicle.status,
        toStatus: status,
        timestamp: new Date(),
        stationId: selectedStation.id,
        userId: user.id,
        userName: user.fullName
      };
      setStatusChangeHistory(prev => [...prev, changeEvent]);
      setStationQueues(prev => ({
        ...prev,
        [selectedStation.id]: (prev[selectedStation.id] || []).map(v => v.id === id ? {
          ...v,
          status,
          serviceStartedAt: status === 'in-service' ? new Date() : v.serviceStartedAt,
          completedAt: status === 'completed' ? new Date() : v.completedAt,
          lastModifiedBy: user.id,
          lastModifiedByName: user.fullName
        } : v)
      }));
    }
  };
  const removeVehicle = (id: string) => {
    if (!selectedStation || !canRemoveVehicle(selectedStation.id)) return;
    setStationQueues(prev => ({
      ...prev,
      [selectedStation.id]: (prev[selectedStation.id] || []).filter(v => v.id !== id)
    }));
  };
  const filteredQueue = currentQueue.filter((vehicle, index) => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase().trim();
    const position = (index + 1).toString();
    const plateNumber = vehicle.plateNumber.toLowerCase();
    const ticketNumber = vehicle.ticketNumber.toLowerCase();
    return plateNumber.includes(search) || position.includes(search) || ticketNumber.includes(search);
  });
  const waitingCount = currentQueue.filter(v => v.status === 'waiting').length;
  const inServiceCount = currentQueue.filter(v => v.status === 'in-service').length;
  const handleNavigateToVehicle = (vehicleId: string) => {
    const element = document.getElementById(`vehicle-${vehicleId}`);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      element.classList.add('ring-4', 'ring-blue-500');
      setTimeout(() => {
        element.classList.remove('ring-4', 'ring-blue-500');
      }, 2000);
    }
  };
  const handleAddStation = (newStation: Omit<GasStation, 'id'>) => {
    const id = (stations.length + 1).toString();
    const stationWithId: GasStation = {
      ...newStation,
      id
    };
    setStations(prev => [...prev, stationWithId]);
  };
  const handleUpdateStation = (stationId: string, updates: Partial<GasStation>) => {
    setStations(prev => prev.map(station => station.id === stationId ? {
      ...station,
      ...updates
    } : station));
    if (selectedStation?.id === stationId) {
      setSelectedStation(prev => prev ? {
        ...prev,
        ...updates
      } : null);
    }
  };
  const handleAddUser = (newUser: Omit<User, 'id'>) => {
    const id = (users.length + 1).toString();
    const userWithId: User = {
      ...newUser,
      id
    };
    setUsers(prev => [...prev, userWithId]);
  };
  const handleUpdateUser = (userId: string, updates: Partial<User>) => {
    setUsers(prev => prev.map(u => u.id === userId ? {
      ...u,
      ...updates
    } : u));
  };
  const handleDeleteUser = (userId: string) => {
    // Ne pas permettre de supprimer l'utilisateur actuellement connecté
    if (user?.id === userId) {
      alert('Vous ne pouvez pas supprimer votre propre compte');
      return;
    }
    setUsers(prev => prev.filter(u => u.id !== userId));
  };
  const getRoleBadge = () => {
    if (!user) return null;
    const roleColors = {
      admin: 'bg-gradient-to-r from-purple-600 to-pink-600',
      supervisor: 'bg-gradient-to-r from-blue-600 to-indigo-600',
      'general-supervisor': 'bg-gradient-to-r from-orange-600 to-amber-600',
      operator: 'bg-gradient-to-r from-green-600 to-emerald-600'
    };
    const roleLabels = {
      admin: 'Administrateur',
      supervisor: 'Superviseur',
      'general-supervisor': 'Superviseur Général',
      operator: 'Opérateur'
    };
    return <div className={`${roleColors[user.role]} text-white px-4 py-2 rounded-lg flex items-center gap-2`} data-id="element-257">
        <ShieldIcon className="w-4 h-4" data-id="element-258" />
        <span className="font-semibold text-sm" data-id="element-259">{roleLabels[user.role]}</span>
      </div>;
  };
  const canAddToCurrentStation = selectedStation && canAddVehicle(selectedStation.id);
  const canViewOverview = user?.role === 'admin' || user?.role === 'supervisor' || user?.role === 'general-supervisor';
  const isAdmin = user?.role === 'admin';
  return <div className="w-full min-h-screen p-6" data-id="element-260">
      <div className="max-w-7xl mx-auto" data-id="element-261">
        {/* CECOGEC Header */}
        <div className="bg-gradient-to-r from-green-700 via-yellow-600 to-red-700 rounded-2xl shadow-lg p-4 mb-4" data-id="element-262">
          <div className="flex items-center justify-center gap-4" data-id="element-263">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bKrn82xDGtf6CD4q31okdQ/Coat_of_arms_of_Mali.svg" alt="Emblème du Mali" className="w-16 h-16 object-contain" data-id="element-264" />
            <h1 className="text-center text-white text-2xl md:text-3xl font-bold" data-id="element-265">
              Centre de Coordination et de Gestion des Crises et Catastrophes
              (CECOGEC)
            </h1>
          </div>
        </div>
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6" data-id="element-266">
          {/* User Info Bar */}
          <div className="flex flex-col md:flex-row items-center justify-between mb-6 pb-4 border-b-2 border-gray-100 gap-4" data-id="element-267">
            <div className="flex items-center gap-4" data-id="element-268">
              <div className="bg-gradient-to-br from-blue-100 to-indigo-100 p-3 rounded-xl" data-id="element-269">
                <UserIcon className="w-6 h-6 text-blue-600" data-id="element-270" />
              </div>
              <div data-id="element-271">
                <p className="text-sm text-gray-500 font-medium" data-id="element-272">
                  Connecté en tant que
                </p>
                <p className="text-lg font-bold text-gray-900" data-id="element-273">
                  {user?.fullName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto justify-center" data-id="element-274">
              {getRoleBadge()}
              <button onClick={logout} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 md:px-4 py-2 rounded-xl font-semibold transition-colors" data-id="element-275">
                <LogOutIcon className="w-4 h-4" data-id="element-276" />
                <span className="hidden sm:inline" data-id="element-277">Déconnexion</span>
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4" data-id="element-278">
            <div className="flex items-center gap-3" data-id="element-279">
              <div className="bg-blue-600 p-3 rounded-xl" data-id="element-280">
                <GaugeIcon className="w-8 h-8 text-white" data-id="element-281" />
              </div>
              <div data-id="element-282">
                <h1 className="text-3xl font-bold text-gray-900" data-id="element-283">
                  Gestion de File d'Attente
                </h1>
                <p className="text-gray-500" data-id="element-284">Stations Service - Bamako</p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto justify-center" data-id="element-285">
              {user?.role !== 'general-supervisor' && <button onClick={() => setIsQuickAccessOpen(true)} className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-3 md:px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl" data-id="element-286">
                  <SearchIcon className="w-5 h-5" data-id="element-287" />
                  <span className="hidden sm:inline" data-id="element-288">Accès Rapide</span>
                </button>}
              <StationSelector stations={stations} selectedStation={selectedStation} onSelectStation={setSelectedStation} data-id="element-289" />
              {canViewOverview && <button onClick={() => setIsOverviewOpen(true)} className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-3 md:px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl" data-id="element-290">
                  <MapIcon className="w-5 h-5" data-id="element-291" />
                  <span className="hidden sm:inline" data-id="element-292">Vue d'Ensemble</span>
                </button>}
              {isAdmin && <button onClick={() => setIsSettingsOpen(true)} className="flex items-center gap-2 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-3 md:px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl" data-id="element-293">
                  <SettingsIcon className="w-5 h-5" data-id="element-294" />
                  <span className="hidden sm:inline" data-id="element-295">Paramétrage</span>
                </button>}
            </div>
          </div>
          {/* Search Bar - Full Width */}
          <div className="mb-6" data-id="element-296">
            <div className="relative" data-id="element-297">
              <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-298" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Rechercher par ticket, immatriculation ou n° de rang..." className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors" data-id="element-299" />
            </div>
            {searchTerm && <p className="text-sm text-gray-600 mt-2" data-id="element-300">
                {filteredQueue.length} résultat
                {filteredQueue.length !== 1 ? 's' : ''} trouvé
                {filteredQueue.length !== 1 ? 's' : ''}
              </p>}
          </div>
          {/* Permission Warning */}
          {selectedStation && !canAddToCurrentStation && <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 mb-4" data-id="element-301">
              <div className="flex items-center gap-3" data-id="element-302">
                <AlertCircleIcon className="w-5 h-5 text-orange-600" data-id="element-303" />
                <p className="text-sm font-semibold text-orange-800" data-id="element-304">
                  Vous ne pouvez gérer que la station qui vous est assignée
                </p>
              </div>
            </div>}
          {/* Selected Station Info */}
          {selectedStation && <div className={`p-4 rounded-xl border-2 mb-4 ${selectedStation.isOperational ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200' : 'bg-gradient-to-r from-red-50 to-orange-50 border-red-200'}`} data-id="element-305">
              <div className="flex items-start justify-between mb-3" data-id="element-306">
                <div className="flex-1" data-id="element-307">
                  <div className="flex items-center gap-2 mb-1" data-id="element-308">
                    <p className="text-sm text-blue-700 font-medium" data-id="element-309">
                      Station Sélectionnée
                    </p>
                    {selectedStation.isOperational ? <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-310" /> : <AlertCircleIcon className="w-4 h-4 text-red-600" data-id="element-311" />}
                  </div>
                  <div className="flex items-center gap-3 mb-2" data-id="element-312">
                    <p className="text-lg font-bold text-blue-900" data-id="element-313">
                      {selectedStation.name}
                    </p>
                    <span className="px-3 py-1 bg-blue-600 text-white text-xs font-bold rounded-full" data-id="element-314">
                      {selectedStation.code}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-1" data-id="element-315">
                    <p className="text-sm text-blue-600" data-id="element-316">
                      {selectedStation.location} • {selectedStation.commune}
                    </p>
                    <span className={`px-2 py-1 rounded-md text-xs font-bold ${selectedStation.riverSide === 'RG' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-317">
                      {selectedStation.riverSide === 'RG' ? 'Rive Gauche' : 'Rive Droite'}
                    </span>
                  </div>
                </div>
                <div className="text-right" data-id="element-318">
                  <p className="text-sm text-blue-700 font-medium" data-id="element-319">
                    Pompes Disponibles
                  </p>
                  <p className="text-2xl font-bold text-blue-900" data-id="element-320">
                    {selectedStation.pumps}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-id="element-321">
                <div className="bg-white rounded-lg p-3 border border-blue-200" data-id="element-322">
                  <div className="flex items-center gap-2 mb-2" data-id="element-323">
                    <ClockIcon className="w-4 h-4 text-blue-600" data-id="element-324" />
                    <p className="text-xs font-semibold text-gray-700" data-id="element-325">
                      Horaires
                    </p>
                  </div>
                  <p className="text-sm font-bold text-gray-900" data-id="element-326">
                    {selectedStation.openingHours}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-200" data-id="element-327">
                  <div className="flex items-center gap-2 mb-2" data-id="element-328">
                    <PhoneIcon className="w-4 h-4 text-blue-600" data-id="element-329" />
                    <p className="text-xs font-semibold text-gray-700" data-id="element-330">
                      Contact Gérant
                    </p>
                  </div>
                  <p className="text-sm font-bold text-gray-900" data-id="element-331">
                    {selectedStation.managerContact}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-blue-200" data-id="element-332">
                  <div className="flex items-center gap-2 mb-2" data-id="element-333">
                    <GaugeIcon className="w-4 h-4 text-blue-600" data-id="element-334" />
                    <p className="text-xs font-semibold text-gray-700" data-id="element-335">
                      Capacité
                    </p>
                  </div>
                  <p className="text-sm font-bold text-gray-900" data-id="element-336">
                    {(selectedStation.capacity / 1000).toFixed(0)}k Litres
                  </p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3 flex-wrap" data-id="element-337">
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-blue-200" data-id="element-338">
                  <FuelIcon className="w-4 h-4 text-blue-600" data-id="element-339" />
                  <span className="text-xs font-semibold text-gray-700" data-id="element-340">
                    Essence:
                  </span>
                  <span className="text-sm font-bold text-blue-900" data-id="element-341">
                    {selectedStation.prices.essence} FCFA/L
                  </span>
                  {selectedStation.fuelAvailability.essence ? <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-342" /> : <AlertCircleIcon className="w-4 h-4 text-red-600" data-id="element-343" />}
                </div>
                <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-blue-200" data-id="element-344">
                  <FuelIcon className="w-4 h-4 text-orange-600" data-id="element-345" />
                  <span className="text-xs font-semibold text-gray-700" data-id="element-346">
                    Diesel:
                  </span>
                  <span className="text-sm font-bold text-orange-900" data-id="element-347">
                    {selectedStation.prices.diesel} FCFA/L
                  </span>
                  {selectedStation.fuelAvailability.diesel ? <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-348" /> : <AlertCircleIcon className="w-4 h-4 text-red-600" data-id="element-349" />}
                </div>
                {selectedStation.prices.gpl && <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-blue-200" data-id="element-350">
                    <FuelIcon className="w-4 h-4 text-purple-600" data-id="element-351" />
                    <span className="text-xs font-semibold text-gray-700" data-id="element-352">
                      GPL:
                    </span>
                    <span className="text-sm font-bold text-purple-900" data-id="element-353">
                      {selectedStation.prices.gpl} FCFA/L
                    </span>
                    {selectedStation.fuelAvailability.gpl ? <CheckCircleIcon className="w-4 h-4 text-green-600" data-id="element-354" /> : <AlertCircleIcon className="w-4 h-4 text-red-600" data-id="element-355" />}
                  </div>}
              </div>
              {!selectedStation.isOperational && <div className="mt-3 bg-red-100 border border-red-300 rounded-lg px-3 py-2" data-id="element-356">
                  <p className="text-sm font-bold text-red-800" data-id="element-357">
                    ⚠️ Station Hors Service - Ajout de véhicules désactivé
                  </p>
                </div>}
            </div>}
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-358">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200" data-id="element-359">
              <div className="flex items-center gap-3" data-id="element-360">
                <div className="bg-blue-600 p-3 rounded-lg" data-id="element-361">
                  <ClockIcon className="w-6 h-6 text-white" data-id="element-362" />
                </div>
                <div data-id="element-363">
                  <p className="text-sm text-blue-700 font-medium" data-id="element-364">
                    En Attente
                  </p>
                  <p className="text-2xl font-bold text-blue-900" data-id="element-365">
                    {waitingCount}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200" data-id="element-366">
              <div className="flex items-center gap-3" data-id="element-367">
                <div className="bg-green-600 p-3 rounded-lg" data-id="element-368">
                  <GaugeIcon className="w-6 h-6 text-white" data-id="element-369" />
                </div>
                <div data-id="element-370">
                  <p className="text-sm text-green-700 font-medium" data-id="element-371">
                    En Service
                  </p>
                  <p className="text-2xl font-bold text-green-900" data-id="element-372">
                    {inServiceCount}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200" data-id="element-373">
              <div className="flex items-center gap-3" data-id="element-374">
                <div className="bg-purple-600 p-3 rounded-lg" data-id="element-375">
                  <CarIcon className="w-6 h-6 text-white" data-id="element-376" />
                </div>
                <div data-id="element-377">
                  <p className="text-sm text-purple-700 font-medium" data-id="element-378">Total</p>
                  <p className="text-2xl font-bold text-purple-900" data-id="element-379">
                    {currentQueue.length}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Queue List */}
        <div className="bg-white rounded-2xl shadow-lg p-6" data-id="element-380">
          <div className="flex items-center justify-between mb-6" data-id="element-381">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-382">
              File d'Attente - {selectedStation?.name}
            </h2>
            <div className="flex items-center gap-3" data-id="element-383">
              {searchTerm && <p className="text-sm text-gray-600" data-id="element-384">
                  {filteredQueue.length} résultat
                  {filteredQueue.length !== 1 ? 's' : ''} trouvé
                  {filteredQueue.length !== 1 ? 's' : ''}
                </p>}
              <button onClick={() => setIsModalOpen(true)} disabled={!selectedStation?.isOperational || !canAddToCurrentStation} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed" title={!canAddToCurrentStation ? "Vous n'avez pas la permission d'ajouter des véhicules à cette station" : ''} data-id="element-385">
                <PlusIcon className="w-5 h-5" data-id="element-386" />
                Ajouter Véhicule
              </button>
            </div>
          </div>
          {filteredQueue.length === 0 ? <div className="text-center py-12" data-id="element-387">
              <CarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-388" />
              <p className="text-gray-500 text-lg" data-id="element-389">
                {searchTerm ? 'Aucun résultat trouvé' : "Aucun véhicule dans la file d'attente"}
              </p>
              <p className="text-gray-400 text-sm mt-2" data-id="element-390">
                {searchTerm ? 'Essayez une autre recherche' : 'Cliquez sur "Ajouter Véhicule" pour commencer'}
              </p>
            </div> : <div className="space-y-3" data-id="element-391">
              {filteredQueue.map((vehicle, index) => <div key={vehicle.id} id={`vehicle-${vehicle.id}`} data-id="element-392">
                  <QueueItem vehicle={vehicle} position={currentQueue.findIndex(v => v.id === vehicle.id) + 1} currentTime={currentTime} onUpdateStatus={updateVehicleStatus} onRemove={removeVehicle} canUpdateStatus={selectedStation ? canUpdateStatus(selectedStation.id) : false} canRemove={selectedStation ? canRemoveVehicle(selectedStation.id) : false} data-id="element-393" />
                </div>)}
            </div>}
        </div>
      </div>
      {isModalOpen && selectedStation && canAddToCurrentStation && <AddVehicleModal onClose={() => setIsModalOpen(false)} onAdd={addVehicle} maxPumps={selectedStation.pumps} existingPlateNumbers={currentQueue.map(v => v.plateNumber)} data-id="element-394" />}
      {isQuickAccessOpen && selectedStation && <QuickAccessModal onClose={() => setIsQuickAccessOpen(false)} queue={currentQueue} maxPumps={selectedStation.pumps} existingPlateNumbers={currentQueue.map(v => v.plateNumber)} onAdd={addVehicle} onNavigateToVehicle={handleNavigateToVehicle} canAdd={canAddToCurrentStation} data-id="element-395" />}
      {isOverviewOpen && canViewOverview && <StationsOverview onClose={() => setIsOverviewOpen(false)} stations={stations} stationQueues={stationQueues} statusChangeHistory={statusChangeHistory} data-id="element-396" />}
      {isSettingsOpen && isAdmin && <SettingsModal onClose={() => setIsSettingsOpen(false)} stations={stations} onAddStation={handleAddStation} onUpdateStation={handleUpdateStation} users={users} onAddUser={handleAddUser} onUpdateUser={handleUpdateUser} onDeleteUser={handleDeleteUser} currentUserId={user?.id || ''} data-id="element-397" />}
    </div>;
}